import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { getWalletPositions } from '../../services/OrcaService';
import './Orca.css';

function OrcaPositions() {
    const navigate = useNavigate();
    const location = useLocation();
    const { connected, publicKey, wallet } = useWallet();
    const [positions, setPositions] = useState([]);
    const [closedPositions, setClosedPositions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        if (connected && publicKey) loadPositions();
        else setPositions([]);
    }, [connected, publicKey]);

    useEffect(() => {
        if (!publicKey) {
            setClosedPositions([]);
            return;
        }
        setClosedPositions(readClosedPositions(publicKey.toBase58()));
    }, [publicKey]);

    useEffect(() => {
        if (!location.state?.closedPosition && !location.state?.notice) return;
        if (location.state.closedPosition) {
            markClosedPosition(location.state.closedPosition);
        }
        if (location.state.notice) {
            setNotification({ type: 'success', text: location.state.notice });
        }
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname]);

    useEffect(() => {
        if (!connected || !publicKey) return undefined;
        const timer = setInterval(() => {
            loadPositions(true);
        }, 30000);
        return () => clearInterval(timer);
    }, [connected, publicKey]);

    async function loadPositions(silent = false) {
        if (!publicKey) return;
        if (!silent) setLoading(true);
        try {
            const data = await getWalletPositions(publicKey.toBase58());
            setPositions(data.positions || []);
        } catch (err) {
            const text = err.response?.data || err.message || 'Erro ao carregar posições de pool.';
            if (!silent) setNotification({ type: 'error', text });
            else console.error(err);
        } finally {
            if (!silent) setLoading(false);
        }
    }

    function shortAddress(address) {
        if (!address) return '';
        return `${address.slice(0, 4)}...${address.slice(-4)}`;
    }

    function tokenLabel(token) {
        return token?.symbol || shortAddress(token?.mint);
    }

    function tokenIcon(token) {
        if (token?.imageUrl) return <img src={token.imageUrl} alt={tokenLabel(token)} />;
        return <span>{(tokenLabel(token) || '?').slice(0, 1)}</span>;
    }

    function formatNumber(value, maximumFractionDigits = 8) {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return value || '-';
        return parsed.toLocaleString('pt-BR', { maximumFractionDigits });
    }

    function formatCurrency(value) {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return '$ -';
        return parsed.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
    }

    function displayToken(position, side) {
        return position.pool?.display?.[side] || position.pool?.[side];
    }

    function feeText(position) {
        const feeA = position.pendingFees?.tokenA;
        const feeB = position.pendingFees?.tokenB;
        return `${formatNumber(feeA?.amount || 0, 8)} ${tokenLabel(feeA)} / ${formatNumber(feeB?.amount || 0, 8)} ${tokenLabel(feeB)}`;
    }

    function markClosedPosition(positionAddress) {
        if (!publicKey || !positionAddress) return;
        const walletAddress = publicKey.toBase58();
        const next = [...new Set([...readClosedPositions(walletAddress), positionAddress])];
        setClosedPositions(next);
        try {
            sessionStorage.setItem(closedPositionsStorageKey(walletAddress), JSON.stringify(next));
        } catch (err) {
            console.error(err);
        }
    }

    function readClosedPositions(walletAddress) {
        try {
            return JSON.parse(sessionStorage.getItem(closedPositionsStorageKey(walletAddress)) || '[]');
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    function closedPositionsStorageKey(walletAddress) {
        return `orcaClosedPositions:${walletAddress || 'unknown'}`;
    }

    const activePositions = positions.filter(position => Number(position.liquidity || 0) > 0
        && !closedPositions.includes(position.positionAddress));
    const inRangeCount = activePositions.filter(position => position.inRange).length;
    const totalTvl = activePositions.reduce((sum, position) => sum + (Number(position.pool?.tvlUsdc) || 0), 0);

    return (
        <>
            <Menu />
            <main className="content orca-page">
                <div className="orca-container">
                    <section className="orca-hero">
                        <div className="orca-hero-copy">
                            <span className="orca-eyebrow">Liquidity Portfolio</span>
                            <h1>Suas posições em pools</h1>
                            <p>Acompanhe pools onde sua carteira fornece liquidez, faixa de preço, liquidez e taxas disponíveis para coleta.</p>
                            <div className="orca-hero-actions">
                                <button className="orca-primary-btn" onClick={loadPositions} disabled={!connected || loading}>
                                    {loading ? 'Atualizando...' : 'Atualizar posições'}
                                </button>
                                <button className="orca-ghost-btn" onClick={() => navigate('/orca')}>Ver pools</button>
                                <button className="orca-ghost-btn" onClick={() => navigate('/orca/portfolio')}>Meu portfólio</button>
                            </div>
                        </div>
                        <div className="orca-wallet-panel">
                            <WalletMultiButton />
                            <div className="orca-wallet-status">
                                <strong>{connected ? 'Carteira conectada' : 'Conectar carteira'}</strong>
                                <span>{connected && publicKey ? shortAddress(publicKey.toBase58()) : 'Phantom, Solflare ou WalletConnect'}</span>
                                {wallet?.adapter?.name ? <small>{wallet.adapter.name}</small> : <small>Selecione sua carteira.</small>}
                            </div>
                        </div>
                    </section>

                    <section className="orca-stats-grid">
                        <div className="orca-stat-card">
                            <small>Posições</small>
                            <strong>{activePositions.length}</strong>
                        </div>
                        <div className="orca-stat-card">
                            <small>Dentro do range</small>
                            <strong>{inRangeCount}</strong>
                        </div>
                        <div className="orca-stat-card">
                            <small>Pools TVL</small>
                            <strong>{formatCurrency(totalTvl)}</strong>
                        </div>
                        <div className="orca-stat-card">
                            <small>Status</small>
                            <strong>{loading ? 'Sincronizando' : 'Atualizado'}</strong>
                        </div>
                    </section>

                    <section className="orca-market-card portfolio-card">
                        <div className="orca-section-header">
                            <div>
                                <h2>Posições de liquidez</h2>
                                <p>Taxas mostradas são as taxas disponíveis registradas na posição. Histórico já coletado exige indexador/transações.</p>
                            </div>
                        </div>

                        {connected ? (
                            <div className="positions-list">
                                {activePositions.map(position => (
                                    <div className="position-card" key={position.positionAddress}>
                                        <div className="position-card-header">
                                            <div className="orca-token-pair">
                                                <div className="orca-token-icons">
                                                    <div className="orca-token-icon">{tokenIcon(displayToken(position, 'tokenA'))}</div>
                                                    <div className="orca-token-icon second">{tokenIcon(displayToken(position, 'tokenB'))}</div>
                                                </div>
                                                <div>
                                                    <strong>{tokenLabel(displayToken(position, 'tokenA'))} / {tokenLabel(displayToken(position, 'tokenB'))}</strong>
                                                    <small>{shortAddress(position.poolAddress)} · posição {shortAddress(position.positionAddress)}</small>
                                                </div>
                                            </div>
                                            <span className={`position-status ${position.inRange ? 'in-range' : 'out-range'}`}>
                                                {position.inRange ? 'Dentro do range' : 'Fora do range'}
                                            </span>
                                        </div>

                                        <div className="position-grid">
                                            <div>
                                                <small>Liquidez</small>
                                                <strong>{formatNumber(position.liquidity, 0)}</strong>
                                            </div>
                                            <div>
                                                <small>Saldo na posição</small>
                                                <strong>
                                                    {formatNumber(position.tokenAmounts?.tokenA?.amount || 0, 6)} {tokenLabel(position.tokenAmounts?.tokenA)}
                                                    {' / '}
                                                    {formatNumber(position.tokenAmounts?.tokenB?.amount || 0, 6)} {tokenLabel(position.tokenAmounts?.tokenB)}
                                                </strong>
                                            </div>
                                            <div>
                                                <small>Taxas disponíveis</small>
                                                <strong>{feeText(position)}</strong>
                                            </div>
                                            <div>
                                                <small>Range</small>
                                                <strong>{formatNumber(position.range?.min, 8)} - {formatNumber(position.range?.max, 8)}</strong>
                                            </div>
                                        </div>

                                        <div className="orca-actions">
                                            <button className="orca-primary-btn" onClick={() => navigate(`/orca/liquidity/${position.poolAddress}?position=${position.positionAddress}`)}>
                                                Gerenciar posição
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {!activePositions.length && <div className="orca-empty-state">Nenhuma posição ativa encontrada para esta carteira.</div>}
                            </div>
                        ) : (
                            <div className="orca-empty-state">Conecte uma carteira para ver suas posições de liquidez.</div>
                        )}
                    </section>
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default OrcaPositions;
